import { useEffect, useRef, useState } from "react";
import { api, resolveMedia, setAdminToken, getStoredToken } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, UploadCloud, Shuffle, Image as ImageIcon, Copy, Check, Eye, Send, MessageCircle } from "lucide-react";
import { openWhatsApp } from "../lib/whatsapp";
import RichTextEditor from "../components/RichTextEditor";

const TABS = [
  { id: "media", label: "Medios" },
  { id: "articles", label: "Journal" },
  { id: "events", label: "Calendario" },
  { id: "experiences", label: "Experiencias" },
  { id: "concierge", label: "Concierge" },
  { id: "solicitudes", label: "Solicitudes" },
  { id: "submissions", label: "Propuestas" },
  { id: "instagram", label: "Instagram" },
  { id: "newsletter", label: "Boletín" },
  { id: "transcripts", label: "Conversaciones" },
];

const emptyArticle = { title: "", dek: "", category: "Recién Abierto", image: "", body: "", author: "El Editor", featured: false, ink_mark: "", hero_video: "", image_pinned: false };
const emptyEvent = { title: "", date: "", time: "19:00", location: "", category: "Gastronomía", description: "", image: "", price: "", image_pinned: false, cta_type: "none", cta_label: "", cta_url: "", tagline: "", long_description: "", includes: [] };
const emptyExp = { title: "", tagline: "", description: "", includes: [], duration: "", group_size: "", price_from: "", image: "", order: 0, image_pinned: false };

export default function Admin() {
  const [authed, setAuthed] = useState(!!getStoredToken());
  const [pwd, setPwd] = useState("");
  const [tab, setTab] = useState("media");

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { password: pwd });
      setAdminToken(data.token);
      setAuthed(true);
      toast.success("Bienvenido, editor.");
    } catch {
      toast.error("Contraseña incorrecta.");
    }
  };

  const logout = () => { setAdminToken(null); setAuthed(false); };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
        <p className="eyebrow">Acceso Editor</p>
        <h1 className="mt-3 font-serif text-4xl">Redacción</h1>
        <p className="mt-3 text-muted2">Introduzca la contraseña de editor.</p>
        <form onSubmit={login} className="mt-8 space-y-6">
          <input type="password" placeholder="Contraseña" value={pwd} onChange={(e) => setPwd(e.target.value)} className="w-full bg-transparent border-b border-border2 py-3 focus:border-forest" data-testid="admin-password" />
          <button className="btn-primary" data-testid="admin-login-submit">Entrar</button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16" data-testid="admin-panel">
      <div className="flex items-end justify-between border-b border-border2 pb-6">
        <div>
          <p className="eyebrow">Panel Editor</p>
          <h1 className="mt-3 font-serif text-4xl">Redacción</h1>
        </div>
        <button onClick={logout} className="text-xs tracking-[0.2em] uppercase text-subtle hover:text-forest inline-flex items-center gap-2" data-testid="admin-logout"><LogOut size={14}/> Salir</button>
      </div>

      <div className="mt-8 flex gap-8 border-b border-border2 pb-4 flex-wrap">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`text-xs tracking-[0.22em] uppercase pb-2 border-b ${tab === t.id ? "border-forest text-forest" : "border-transparent text-subtle"}`}>{t.label}</button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "media" && <MediaPanel />}
        {tab === "articles" && <CrudPanel resource="articles" emptyItem={emptyArticle} fields={articleFields} title="Artículo" />}
        {tab === "events" && <CrudPanel resource="events" emptyItem={emptyEvent} fields={eventFields} title="Evento" />}
        {tab === "experiences" && <CrudPanel resource="experiences" emptyItem={emptyExp} fields={expFields} title="Experiencia" />}
        {tab === "concierge" && <ConciergePanel />}
        {tab === "solicitudes" && <SolicitudesPanel />}
        {tab === "submissions" && <SubmissionsPanel />}
        {tab === "instagram" && <InstagramPanel />}
        {tab === "newsletter" && <NewsletterPanel />}
        {tab === "transcripts" && <TranscriptsPanel />}
      </div>
    </div>
  );
}

/* ─────────────────────────── MEDIA PANEL ─────────────────────────── */

function MediaPanel() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of Array.from(files)) {
      const form = new FormData();
      form.append("file", f);
      try {
        await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        fail += 1;
        console.error(err);
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} archivo${ok > 1 ? "s" : ""} cargado${ok > 1 ? "s" : ""}.`);
    if (fail) toast.error(`${fail} archivo${fail > 1 ? "s" : ""} no se pudieron cargar.`);
    load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const removeItem = async (id) => {
    if (!window.confirm("¿Eliminar este medio? Las páginas que lo usen mostrarán una imagen rota hasta que se reasigne.")) return;
    await api.delete(`/media/${id}`);
    load();
  };

  const copyUrl = async (item) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error("No se pudo copiar.");
    }
  };

  const runRandomAssign = async () => {
    if (!window.confirm("Se reemplazarán TODAS las imágenes de artículos, eventos, experiencias y la página Concierge con las que están cargadas aquí. ¿Continuar?")) return;
    setAssigning(true);
    try {
      const { data } = await api.post("/media/random-assign", { targets: ["articles", "events", "experiences", "concierge"] });
      const a = data.assigned || {};
      toast.success(`Distribuido: ${a.articles || 0} artículos · ${a.events || 0} eventos · ${a.experiences || 0} experiencias · Concierge actualizado.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falló la distribución.");
    }
    setAssigning(false);
  };

  const imgCount = items.filter((i) => i.kind === "image").length;

  return (
    <div className="space-y-10" data-testid="admin-media-panel">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Dropzone */}
        <div className="lg:col-span-8">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer border-2 border-dashed p-12 text-center transition-colors ${dragOver ? "border-cobalt bg-cobalt/5" : "border-border2 bg-white hover:border-forest"}`}
            data-testid="media-dropzone"
          >
            <UploadCloud size={40} className="mx-auto text-subtle" />
            <p className="mt-4 font-serif text-2xl text-ink">Arrastre y suelte imágenes o video</p>
            <p className="mt-2 text-sm text-subtle">O haga clic para seleccionar. JPG, PNG, WEBP o MP4. Múltiples archivos permitidos.</p>
            <p className="mt-4 text-xs tracking-[0.2em] uppercase text-cobalt">{uploading ? "Cargando…" : "Seleccionar archivos"}</p>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/mp4,video/quicktime,video/webm"
              multiple
              className="hidden"
              onChange={(e) => uploadFiles(e.target.files)}
              data-testid="media-file-input"
            />
          </div>
        </div>

        {/* Random assign */}
        <div className="lg:col-span-4 bg-sand p-6 border border-border2">
          <p className="eyebrow">Distribución aleatoria</p>
          <h3 className="mt-3 font-serif text-2xl text-ink">Repartir en toda la revista</h3>
          <p className="mt-3 text-sm text-muted2">
            Toma sus {imgCount} imagen{imgCount === 1 ? "" : "es"} cargada{imgCount === 1 ? "" : "s"} y las asigna al azar a cada artículo, evento y experiencia.
          </p>
          <button
            onClick={runRandomAssign}
            disabled={assigning || imgCount === 0}
            className="btn-primary mt-5 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="media-random-assign"
          >
            <Shuffle size={14} /> {assigning ? "Distribuyendo…" : "Repartir al azar"}
          </button>
        </div>
      </div>

      {/* Gallery */}
      <div>
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-serif text-2xl">Biblioteca ({items.length})</h2>
          <p className="text-xs tracking-[0.2em] uppercase text-subtle">Haga clic en un medio para copiar su URL</p>
        </div>
        {items.length === 0 ? (
          <div className="border border-border2 bg-white p-10 text-center text-subtle">
            <ImageIcon size={28} className="mx-auto text-border2" />
            <p className="mt-3 text-sm">Aún no hay medios cargados.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="media-gallery">
            {items.map((m) => (
              <div key={m.id} className="group relative border border-border2 bg-white overflow-hidden">
                {m.kind === "image" ? (
                  <img src={resolveMedia(m.url)} alt={m.original_filename} className="w-full h-40 object-cover" />
                ) : (
                  <video src={resolveMedia(m.url)} className="w-full h-40 object-cover bg-black" muted playsInline />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => copyUrl(m)}
                      title="Copiar URL"
                      className="bg-white/95 text-ink p-1.5 hover:bg-white"
                      data-testid={`media-copy-${m.id}`}
                    >
                      {copiedId === m.id ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                    <button
                      onClick={() => removeItem(m.id)}
                      title="Eliminar"
                      className="bg-terracotta/95 text-white p-1.5 hover:bg-terracotta"
                      data-testid={`media-delete-${m.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <p className="text-[0.65rem] text-white/90 line-clamp-2">{m.original_filename}</p>
                </div>
                <span className="absolute top-1 left-1 bg-white/95 text-[0.6rem] tracking-[0.16em] uppercase px-1.5 py-0.5">{m.kind}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────── CRUD PANEL ────────────────────────── */

const articleFields = [
  { key: "title", label: "Título", type: "text" },
  { key: "dek", label: "Bajada", type: "textarea" },
  { key: "category", label: "Categoría", type: "select", options: ["Recién Abierto","Esta Semana","Para Reiniciar","Gastronomía","Guía de Temporada"] },
  { key: "image", label: "Imagen", type: "media", mediaKind: "image" },
  { key: "image_pinned", label: "Fijar imagen (protegerla del \"Repartir al azar\")", type: "checkbox" },
  { key: "author", label: "Autor", type: "text" },
  { key: "body", label: "Cuerpo", type: "richtext" },
  { key: "ink_mark", label: "Ilustración a tinta", type: "select", options: ["", "paloma", "ocote", "velero", "flourish", "waves", "wineglass", "leaf"] },
  { key: "hero_video", label: "Video de portada (opcional)", type: "media", mediaKind: "video" },
  { key: "featured", label: "En rotación de portada", type: "checkbox" },
];

const eventFields = [
  { key: "title", label: "Título", type: "text" },
  { key: "date", label: "Fecha", type: "date" },
  { key: "time", label: "Hora", type: "text", placeholder: "19:00" },
  { key: "location", label: "Ubicación", type: "text" },
  { key: "category", label: "Categoría", type: "select", options: ["Gastronomía","Cultura","Bienestar","Privado"] },
  { key: "description", label: "Descripción corta (lista + hover)", type: "textarea" },
  { key: "tagline", label: "Frase corta (cursiva, página de detalle)", type: "text" },
  { key: "long_description", label: "Descripción larga (página de detalle)", type: "textarea", rows: 6 },
  { key: "includes", label: "Qué incluye (una línea por punto)", type: "list" },
  { key: "image", label: "Imagen", type: "media", mediaKind: "image" },
  { key: "image_pinned", label: "Fijar imagen (protegerla del \"Repartir al azar\")", type: "checkbox" },
  { key: "price", label: "Precio", type: "text" },
  { key: "cta_type", label: "Tipo de botón", type: "select", options: ["none","whatsapp","external"] },
  { key: "cta_label", label: "Texto del botón (ej. \"Comprar boletos\", \"Reservar lugar\", \"Visitar sitio\")", type: "text" },
  { key: "cta_url", label: "URL externa (solo si tipo = external)", type: "text", placeholder: "https://…" },
];

const expFields = [
  { key: "title", label: "Título", type: "text" },
  { key: "tagline", label: "Frase corta", type: "text" },
  { key: "description", label: "Descripción", type: "textarea" },
  { key: "duration", label: "Duración", type: "text" },
  { key: "group_size", label: "Grupo", type: "text" },
  { key: "price_from", label: "Precio desde", type: "text" },
  { key: "image", label: "Imagen", type: "media", mediaKind: "image" },
  { key: "image_pinned", label: "Fijar imagen (protegerla del \"Repartir al azar\")", type: "checkbox" },
  { key: "order", label: "Orden", type: "number" },
  { key: "includes", label: "Qué incluye (una línea por punto)", type: "list" },
];

function CrudPanel({ resource, emptyItem, fields, title }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyItem);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get(`/${resource}`).then((r) => setItems(r.data));

  useEffect(() => { load(); }, [resource]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/${resource}/${editingId}`, form);
        toast.success(`${title} actualizado`);
      } else {
        await api.post(`/${resource}`, form);
        toast.success(`${title} creado`);
      }
      setForm(emptyItem); setEditingId(null); load();
    } catch { toast.error("Falló la operación"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    await api.delete(`/${resource}/${id}`);
    load();
  };

  const edit = (it) => { setForm({ ...emptyItem, ...it }); setEditingId(it.id); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <form onSubmit={submit} className="lg:col-span-5 space-y-5" data-testid={`admin-form-${resource}`}>
        <h2 className="font-serif text-2xl">{editingId ? `Editar ${title}` : `Nuevo ${title}`}</h2>
        {fields.map((f) => (
          <div key={f.key}>
            <label className="eyebrow">{f.label}</label>
            {f.type === "textarea" && (
              <textarea rows={f.rows || 3} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-2 w-full bg-white border border-border2 p-3 focus:border-forest" />
            )}
            {f.type === "richtext" && (
              <RichTextEditor
                value={form[f.key] || ""}
                onChange={(html) => setForm({ ...form, [f.key]: html })}
              />
            )}
            {f.type === "text" && (
              <input value={form[f.key] || ""} placeholder={f.placeholder} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-2 w-full bg-white border border-border2 p-3 focus:border-forest" />
            )}
            {f.type === "date" && (
              <input type="date" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-2 w-full bg-white border border-border2 p-3 focus:border-forest" />
            )}
            {f.type === "number" && (
              <input type="number" value={form[f.key] ?? 0} onChange={(e) => setForm({ ...form, [f.key]: parseInt(e.target.value || "0", 10) })} className="mt-2 w-full bg-white border border-border2 p-3 focus:border-forest" />
            )}
            {f.type === "checkbox" && (
              <label className="mt-2 inline-flex items-center gap-3 text-sm text-ink cursor-pointer">
                <span className={`relative inline-block w-10 h-6 rounded-full transition-colors ${form[f.key] ? "bg-cobalt" : "bg-black/15"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form[f.key] ? "translate-x-4" : ""}`} />
                </span>
                <input type="checkbox" className="hidden" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} />
                <span className="font-display text-[0.68rem] tracking-[0.24em] uppercase text-cobalt font-semibold">{form[f.key] ? "Activado" : "Desactivado"}</span>
              </label>
            )}
            {f.type === "select" && (
              <select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-2 w-full bg-white border border-border2 p-3">
                {f.options.map((o) => <option key={o}>{o}</option>)}
              </select>
            )}
            {f.type === "list" && (
              <textarea rows={4} value={(form[f.key] || []).join("\n")} onChange={(e) => setForm({ ...form, [f.key]: e.target.value.split("\n").filter(Boolean) })} className="mt-2 w-full bg-white border border-border2 p-3 focus:border-forest" />
            )}
            {f.type === "media" && (
              <MediaPicker
                value={form[f.key]}
                kind={f.mediaKind}
                onChange={(url) => setForm({ ...form, [f.key]: url })}
              />
            )}
          </div>
        ))}
        <div className="flex gap-3">
          <button className="btn-primary" data-testid={`admin-save-${resource}`}><Plus size={14}/> {editingId ? "Guardar" : "Crear"}</button>
          {editingId && <button type="button" onClick={() => { setForm(emptyItem); setEditingId(null); }} className="btn-outline">Cancelar</button>}
        </div>
      </form>

      <div className="lg:col-span-7">
        <h2 className="font-serif text-2xl mb-6">Publicados ({items.length})</h2>
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.id} className="border border-border2 bg-white p-4 flex gap-4" data-testid={`admin-item-${it.id}`}>
              {it.image && <img src={resolveMedia(it.image)} alt="" className="w-24 h-24 object-cover" />}
              <div className="flex-1">
                <p className="font-serif text-lg text-ink flex items-center gap-2">
                  {it.title}
                  {it.image_pinned && <span className="text-[0.6rem] tracking-[0.2em] uppercase bg-cobalt text-white px-2 py-0.5" data-testid={`pinned-badge-${it.id}`}>Fijada</span>}
                </p>
                <p className="text-xs text-subtle mt-1 line-clamp-2">{it.dek || it.description || it.tagline}</p>
                <p className="text-xs text-terracotta uppercase tracking-widest mt-1">{it.category || it.date || ""}</p>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => edit(it)} className="text-xs uppercase tracking-widest text-forest">Editar</button>
                <button onClick={() => remove(it.id)} className="text-xs uppercase tracking-widest text-terracotta inline-flex items-center gap-1"><Trash2 size={12}/> Borrar</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ───────── Media picker (used inside CRUD forms) ───────── */

function MediaPicker({ value, kind = "image", onChange }) {
  const [open, setOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) api.get(`/media?kind=${kind}`).then((r) => setLibrary(r.data)).catch(() => {});
  }, [open, kind]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", files[0]);
      const { data } = await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Cargado");
      setOpen(false);
    } catch {
      toast.error("Falló la carga");
    }
    setUploading(false);
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-3">
        {value ? (
          kind === "video" ? (
            <video src={resolveMedia(value)} className="w-20 h-20 object-cover bg-black border border-border2" muted />
          ) : (
            <img src={resolveMedia(value)} alt="" className="w-20 h-20 object-cover border border-border2" />
          )
        ) : (
          <div className="w-20 h-20 border border-dashed border-border2 flex items-center justify-center text-subtle">
            <ImageIcon size={20} />
          </div>
        )}
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => inputRef.current?.click()} className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1">
              <UploadCloud size={12}/> {uploading ? "Cargando…" : "Subir"}
            </button>
            <button type="button" onClick={() => setOpen(true)} className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1">
              <ImageIcon size={12}/> Biblioteca
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="text-xs text-terracotta uppercase tracking-widest">Quitar</button>
            )}
          </div>
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…o pega una URL" className="w-full bg-white border border-border2 p-2 text-sm focus:border-forest" />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={kind === "video" ? "video/mp4,video/quicktime,video/webm" : "image/*"}
          className="hidden"
          onChange={(e) => upload(e.target.files)}
        />
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-end justify-between border-b border-border2 pb-3 mb-4">
              <h3 className="font-serif text-2xl">Biblioteca de {kind === "video" ? "video" : "imágenes"}</h3>
              <button onClick={() => setOpen(false)} className="text-xs uppercase tracking-widest text-subtle">Cerrar</button>
            </div>
            {library.length === 0 ? (
              <p className="text-sm text-subtle py-8 text-center">No hay {kind === "video" ? "videos" : "imágenes"} en la biblioteca.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {library.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { onChange(m.url); setOpen(false); }}
                    className="border border-border2 hover:border-cobalt overflow-hidden"
                  >
                    {m.kind === "image" ? (
                      <img src={resolveMedia(m.url)} alt="" className="w-full h-28 object-cover" />
                    ) : (
                      <video src={resolveMedia(m.url)} className="w-full h-28 object-cover bg-black" muted />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* ────────────────────── CONCIERGE PANEL ────────────────────── */

function ConciergePanel() {
  const [hero, setHero] = useState("");
  const [strip, setStrip] = useState(["", "", ""]);
  const [pinned, setPinned] = useState({ hero: false, strip_0: false, strip_1: false, strip_2: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site/concierge").then((r) => {
      setHero(r.data?.hero || "");
      const s = r.data?.strip || [];
      setStrip([s[0] || "", s[1] || "", s[2] || ""]);
      setPinned(r.data?.pinned || { hero: false, strip_0: false, strip_1: false, strip_2: false });
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/site/concierge", { hero, strip, pinned });
      toast.success("Concierge actualizado.");
    } catch {
      toast.error("Falló la actualización.");
    }
    setSaving(false);
  };

  const PinToggle = ({ slot, testid }) => (
    <label className="mt-2 inline-flex items-center gap-2 text-xs text-ink cursor-pointer" data-testid={testid}>
      <input type="checkbox" className="hidden" checked={!!pinned[slot]} onChange={(e) => setPinned({ ...pinned, [slot]: e.target.checked })} />
      <span className={`relative inline-block w-8 h-5 rounded-full transition-colors ${pinned[slot] ? "bg-cobalt" : "bg-black/15"}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${pinned[slot] ? "translate-x-3" : ""}`} />
      </span>
      <span className="font-display text-[0.6rem] tracking-[0.22em] uppercase text-cobalt font-semibold">{pinned[slot] ? "Fijada" : "Al azar"}</span>
    </label>
  );

  return (
    <form onSubmit={save} className="max-w-3xl space-y-8" data-testid="admin-concierge-panel">
      <div>
        <h2 className="font-serif text-2xl">Página Concierge</h2>
        <p className="mt-2 text-sm text-muted2">Imágenes que aparecen en la portada de Concierge y en el tríptico "La Casa Lista". Fija una imagen para que "Repartir al azar" no la sobreescriba.</p>
      </div>

      <div>
        <label className="eyebrow">Imagen de portada (hero)</label>
        <MediaPicker value={hero} kind="image" onChange={setHero} />
        <PinToggle slot="hero" testid="pin-concierge-hero" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[0, 1, 2].map((i) => (
          <div key={i}>
            <label className="eyebrow">Tríptico · {["el almuerzo", "la cava", "el pan"][i]}</label>
            <MediaPicker
              value={strip[i]}
              kind="image"
              onChange={(v) => {
                const next = [...strip];
                next[i] = v;
                setStrip(next);
              }}
            />
            <PinToggle slot={`strip_${i}`} testid={`pin-concierge-strip-${i}`} />
          </div>
        ))}
      </div>

      <button disabled={saving} className="btn-primary disabled:opacity-50" data-testid="concierge-save">
        {saving ? "Guardando…" : "Guardar"}
      </button>
    </form>
  );
}

/* ────────────────────── SUBMISSIONS PANEL ────────────────────── */

const SUBMISSION_STATUSES = [
  { id: "new", label: "Nueva" },
  { id: "reviewing", label: "Revisando" },
  { id: "accepted", label: "Aceptada" },
  { id: "declined", label: "Rechazada" },
];

function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/submissions").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/submissions/${id}`, { status });
      toast.success("Estado actualizado");
      load();
    } catch { toast.error("No se pudo actualizar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta propuesta?")) return;
    await api.delete(`/submissions/${id}`);
    load();
  };

  const filtered = filter === "all" ? items : items.filter((s) => s.status === filter);
  const counts = SUBMISSION_STATUSES.reduce((acc, s) => ({ ...acc, [s.id]: items.filter((i) => i.status === s.id).length }), {});

  return (
    <div className="space-y-8" data-testid="admin-submissions-panel">
      <div>
        <h2 className="font-serif text-2xl">Propuestas recibidas ({items.length})</h2>
        <p className="mt-2 text-sm text-muted2">Lectores u organizadores que piden aparecer en el Insider. Cada envío llega también a su correo.</p>
      </div>
      <div className="flex gap-6 border-b border-border2 pb-4 flex-wrap">
        <button onClick={() => setFilter("all")} className={`text-xs tracking-[0.22em] uppercase pb-2 border-b ${filter === "all" ? "border-forest text-forest" : "border-transparent text-subtle"}`} data-testid="sub-filter-all">Todas · {items.length}</button>
        {SUBMISSION_STATUSES.map((s) => (
          <button key={s.id} onClick={() => setFilter(s.id)} data-testid={`sub-filter-${s.id}`} className={`text-xs tracking-[0.22em] uppercase pb-2 border-b ${filter === s.id ? "border-forest text-forest" : "border-transparent text-subtle"}`}>
            {s.label} · {counts[s.id] || 0}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-subtle">Sin propuestas en esta vista.</p>
      ) : (
        <div className="space-y-5">
          {filtered.map((s) => (
            <div key={s.id} className="border border-border2 bg-white p-6" data-testid={`sub-${s.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="eyebrow">{s.category || "Sin categoría"} · {s.event_date || "fecha abierta"}</p>
                  <h3 className="mt-2 font-serif text-xl text-ink">{s.event_name}</h3>
                  <p className="mt-1 text-sm text-subtle">Por <strong className="text-ink">{s.name}</strong>{s.organization ? ` · ${s.organization}` : ""} · <a href={`mailto:${s.contact}`} className="text-cobalt border-b border-cobalt/40">{s.contact}</a></p>
                  {s.link && <p className="mt-1 text-xs"><a href={s.link} target="_blank" rel="noopener noreferrer" className="text-cobalt break-all underline underline-offset-2">{s.link}</a></p>}
                  <p className="mt-4 text-muted2 text-sm leading-relaxed">{s.why_fits}</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <select value={s.status} onChange={(e) => updateStatus(s.id, e.target.value)} data-testid={`sub-status-${s.id}`} className="bg-white border border-border2 p-2 text-xs uppercase tracking-widest">
                    {SUBMISSION_STATUSES.map((st) => <option key={st.id} value={st.id}>{st.label}</option>)}
                  </select>
                  <button onClick={() => remove(s.id)} className="text-xs uppercase tracking-widest text-terracotta inline-flex items-center gap-1 self-end" data-testid={`sub-delete-${s.id}`}><Trash2 size={12}/> Borrar</button>
                </div>
              </div>
              <p className="mt-4 text-[0.6rem] tracking-[0.22em] uppercase text-subtle font-display font-semibold">
                Recibida {new Date(s.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────────────────── INSTAGRAM PANEL ────────────────────── */

function InstagramPanel() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site/instagram").then((r) => setItems(r.data?.items || [])).catch(() => {});
  }, []);

  const addItem = () => setItems([...items, { image: "", link: "", caption: "" }]);
  const updateItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const target = i + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[i], next[target]] = [next[target], next[i]];
    setItems(next);
  };

  const save = async () => {
    const cleaned = items.filter((it) => it.image);
    setSaving(true);
    try {
      await api.put("/site/instagram", { items: cleaned });
      setItems(cleaned);
      toast.success("Galería actualizada");
    } catch { toast.error("No se pudo guardar"); }
    setSaving(false);
  };

  return (
    <div className="space-y-8" data-testid="admin-instagram-panel">
      <div>
        <h2 className="font-serif text-2xl">Galería Instagram</h2>
        <p className="mt-2 text-sm text-muted2">Las imágenes aparecen en una tira al pie del sitio. Reordene con las flechas; haga clic en Guardar para publicar.</p>
      </div>

      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={i} className="border border-border2 bg-white p-4 flex gap-4 items-start" data-testid={`ig-row-${i}`}>
            <div className="font-serif text-3xl text-cobalt-deep w-10 shrink-0">{String(i + 1).padStart(2, "0")}</div>
            <div className="flex-1 space-y-3 min-w-0">
              <div>
                <label className="eyebrow">Imagen</label>
                <MediaPicker value={it.image} kind="image" onChange={(v) => updateItem(i, { image: v })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={it.link || ""} onChange={(e) => updateItem(i, { link: e.target.value })} placeholder="URL del post (opcional)" className="w-full bg-white border border-border2 p-2 text-sm" data-testid={`ig-link-${i}`} />
                <input value={it.caption || ""} onChange={(e) => updateItem(i, { caption: e.target.value })} placeholder="Texto alternativo" className="w-full bg-white border border-border2 p-2 text-sm" data-testid={`ig-caption-${i}`} />
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-xs uppercase tracking-widest text-cobalt disabled:text-subtle disabled:cursor-not-allowed">↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-xs uppercase tracking-widest text-cobalt disabled:text-subtle disabled:cursor-not-allowed">↓</button>
              <button type="button" onClick={() => removeItem(i)} className="text-xs uppercase tracking-widest text-terracotta inline-flex items-center gap-1 mt-2" data-testid={`ig-delete-${i}`}><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-subtle border border-dashed border-border2 p-6 text-center">Agregue su primera imagen de Instagram.</p>}
      </div>

      <div className="flex gap-4">
        <button type="button" onClick={addItem} className="btn-outline inline-flex items-center gap-1" data-testid="ig-add"><Plus size={14}/> Agregar imagen</button>
        <button type="button" disabled={saving} onClick={save} className="btn-primary disabled:opacity-50" data-testid="ig-save">{saving ? "Guardando…" : "Guardar galería"}</button>
      </div>
    </div>
  );
}


/* ────────────────────── NEWSLETTER PANEL ────────────────────── */

function NewsletterPanel() {
  const [subs, setSubs] = useState({ total: 0, active: 0, subscribers: [] });
  const [digests, setDigests] = useState([]);
  const [filter, setFilter] = useState("active");
  const [sending, setSending] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [articles, setArticles] = useState([]);
  const [coverId, setCoverId] = useState("");

  const loadSubs = () => api.get("/subscribers").then((r) => setSubs(r.data)).catch(() => {});
  const loadDigests = () => api.get("/admin/digests").then((r) => setDigests(r.data || [])).catch(() => {});
  const loadArticles = () => api.get("/articles").then((r) => setArticles(r.data || [])).catch(() => {});
  const loadCover = () => api.get("/admin/digests/cover").then((r) => setCoverId(r.data?.cover_article_id || "")).catch(() => {});

  useEffect(() => {
    loadSubs();
    loadDigests();
    loadArticles();
    loadCover();
  }, []);

  const saveCover = async (id) => {
    try {
      await api.put("/admin/digests/cover", { cover_article_id: id });
      setCoverId(id);
      toast.success(id ? "Portada actualizada" : "Portada automática restaurada");
    } catch { toast.error("No se pudo guardar la portada"); }
  };

  const loadPreview = async () => {
    try {
      const r = await api.get("/admin/digests/preview");
      setPreviewHtml(r.data);
      setShowPreview(true);
    } catch { toast.error("No se pudo generar la vista previa"); }
  };

  const sendNow = async () => {
    if (!window.confirm(`Se enviará el digest a ${subs.active} suscriptor${subs.active === 1 ? "" : "es"} activo${subs.active === 1 ? "" : "s"}. ¿Continuar?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/send-digest");
      toast.success(`Enviado a ${r.data.subscribers} suscriptor${r.data.subscribers === 1 ? "" : "es"}.`);
      loadSubs();
      loadDigests();
    } catch { toast.error("Falló el envío"); }
    setSending(false);
  };

  const retryFailures = async (runId) => {
    if (!window.confirm("Se reintentará el envío solo a los correos que fallaron. ¿Continuar?")) return;
    setRetryingId(runId);
    try {
      const r = await api.post(`/admin/digests/${runId}/retry`);
      toast.success(`Reintentando ${r.data.retrying} correo${r.data.retrying === 1 ? "" : "s"}.`);
      setTimeout(loadDigests, 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reintentar");
    }
    setRetryingId(null);
  };

  const deleteSubscriber = async (sub) => {
    if (!window.confirm(`¿Eliminar definitivamente ${sub.email} de la lista?`)) return;
    try {
      await api.delete(`/subscribers/${sub.id}`);
      toast.success("Eliminado");
      loadSubs();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const filteredSubs = (subs.subscribers || []).filter((s) =>
    filter === "all" ? true : filter === "active" ? !s.unsubscribed_at : !!s.unsubscribed_at
  );

  const downloadCsv = () => {
    const rows = [["email","estado","alta","baja","origen"]];
    (subs.subscribers || []).forEach((s) => rows.push([
      s.email,
      s.unsubscribed_at ? "Baja" : "Activo",
      s.subscribed_at || "",
      s.unsubscribed_at || "",
      s.source || "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `insider-suscriptores-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-14" data-testid="admin-newsletter-panel">
      {/* Stats + actions */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-5 bg-sand p-7 border border-border2">
          <p className="eyebrow">Lista de suscriptores</p>
          <h2 className="mt-3 font-serif text-5xl text-cobalt-deep leading-none">{subs.active}</h2>
          <p className="mt-2 text-sm text-muted2">activo{subs.active === 1 ? "" : "s"} · {subs.total} total</p>
          <div className="mt-6 flex gap-3 flex-wrap">
            <button onClick={downloadCsv} className="btn-outline text-xs px-4 py-2" data-testid="newsletter-export">Exportar CSV</button>
          </div>
        </div>
        <div className="md:col-span-7 bg-white p-7 border border-cobalt/40">
          <p className="eyebrow">Edición del jueves</p>
          <h3 className="mt-3 font-serif text-2xl text-ink leading-snug">Esta semana en el Valle</h3>
          <p className="mt-2 text-sm text-muted2">Se envía automáticamente los jueves a las 8 a.m. (hora de la Ciudad de México).</p>
          <div className="mt-5">
            <label className="eyebrow">Portada del boletín</label>
            <select
              value={coverId}
              onChange={(e) => saveCover(e.target.value)}
              data-testid="newsletter-cover-select"
              className="mt-2 w-full bg-white border border-border2 p-2 text-sm focus:border-forest"
            >
              <option value="">— Automática (artículo más reciente) —</option>
              {articles.map((a) => (
                <option key={a.id} value={a.id}>{a.title}</option>
              ))}
            </select>
            <p className="mt-1 text-[0.68rem] text-subtle">La portada va con foto grande arriba del digest.</p>
          </div>
          <div className="mt-6 flex gap-3 flex-wrap">
            <button onClick={loadPreview} className="btn-outline text-xs px-4 py-2 inline-flex items-center gap-2" data-testid="newsletter-preview-btn">
              <Eye size={13}/> Vista previa
            </button>
            <button onClick={sendNow} disabled={sending || subs.active === 0} className="btn-primary text-xs px-4 py-2 disabled:opacity-50 inline-flex items-center gap-2" data-testid="newsletter-send-now">
              <Send size={13}/> {sending ? "Enviando…" : "Enviar edición ahora"}
            </button>
          </div>
        </div>
      </div>

      {/* Subscribers table */}
      <div>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
          <h2 className="font-serif text-2xl">Suscriptores</h2>
          <div className="flex gap-5">
            <button onClick={() => setFilter("active")} data-testid="sub-filter-active" className={`text-xs tracking-[0.22em] uppercase pb-1 border-b ${filter === "active" ? "border-forest text-forest" : "border-transparent text-subtle"}`}>Activos · {subs.active}</button>
            <button onClick={() => setFilter("inactive")} data-testid="sub-filter-inactive" className={`text-xs tracking-[0.22em] uppercase pb-1 border-b ${filter === "inactive" ? "border-forest text-forest" : "border-transparent text-subtle"}`}>Bajas · {subs.total - subs.active}</button>
            <button onClick={() => setFilter("all")} data-testid="sub-filter-all-sub" className={`text-xs tracking-[0.22em] uppercase pb-1 border-b ${filter === "all" ? "border-forest text-forest" : "border-transparent text-subtle"}`}>Todos · {subs.total}</button>
          </div>
        </div>
        {filteredSubs.length === 0 ? (
          <p className="text-sm text-subtle border border-dashed border-border2 p-6 text-center">Sin suscriptores en esta vista.</p>
        ) : (
          <div className="border border-border2 bg-white overflow-hidden" data-testid="subscribers-list">
            <table className="w-full">
              <thead className="bg-sand text-[0.6rem] tracking-[0.22em] uppercase text-cobalt font-display font-semibold">
                <tr>
                  <th className="text-left px-5 py-3">Correo</th>
                  <th className="text-left px-5 py-3">Estado</th>
                  <th className="text-left px-5 py-3">Alta</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Origen</th>
                  <th className="text-right px-5 py-3 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {filteredSubs.map((s, i) => (
                  <tr key={s.id} className={`text-sm text-ink ${i % 2 === 0 ? "bg-white" : "bg-sand/40"}`} data-testid={`subscriber-${s.id}`}>
                    <td className="px-5 py-3 font-mono text-[0.84rem]">{s.email}</td>
                    <td className="px-5 py-3">
                      {s.unsubscribed_at ? (
                        <span className="text-[0.6rem] tracking-[0.2em] uppercase bg-terracotta/20 text-terracotta px-2 py-0.5">Baja</span>
                      ) : (
                        <span className="text-[0.6rem] tracking-[0.2em] uppercase bg-cobalt/15 text-cobalt px-2 py-0.5">Activo</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-muted2 text-xs">{new Date(s.subscribed_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}</td>
                    <td className="px-5 py-3 text-muted2 text-xs hidden md:table-cell">{s.source || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => deleteSubscriber(s)}
                        title="Eliminar"
                        className="text-terracotta/80 hover:text-terracotta"
                        data-testid={`subscriber-delete-${s.id}`}
                      >
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Digest history */}
      <div>
        <h2 className="font-serif text-2xl mb-5">Envíos del jueves ({digests.length})</h2>
        {digests.length === 0 ? (
          <p className="text-sm text-subtle border border-dashed border-border2 p-6 text-center">Aún no se ha enviado ninguna edición.</p>
        ) : (
          <div className="space-y-4" data-testid="digest-history">
            {digests.map((d) => (
              <div key={d.run_id} className="border border-border2 bg-white p-6" data-testid={`digest-${d.run_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="eyebrow">
                      {d.triggered_by === "manual" ? "Envío manual" : d.triggered_by === "retry" ? "Reintento" : "Programado · jueves"}
                    </p>
                    <h3 className="mt-2 font-serif text-xl text-ink">{d.subject || "Esta semana en el Valle"}</h3>
                    <p className="mt-1 text-xs text-subtle">
                      {new Date(d.at).toLocaleString("es-MX", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className="flex gap-6 text-sm shrink-0 items-start">
                    <div>
                      <p className="text-[0.6rem] tracking-[0.22em] uppercase text-subtle">Enviado</p>
                      <p className="mt-1 font-serif text-2xl text-cobalt-deep">{d.sent ?? 0}</p>
                    </div>
                    {(d.failed || 0) > 0 && (
                      <>
                        <div>
                          <p className="text-[0.6rem] tracking-[0.22em] uppercase text-subtle">Falló</p>
                          <p className="mt-1 font-serif text-2xl text-terracotta">{d.failed}</p>
                        </div>
                        <button
                          onClick={() => retryFailures(d.run_id)}
                          disabled={retryingId === d.run_id}
                          className="btn-outline text-xs px-3 py-2 inline-flex items-center gap-2 self-center disabled:opacity-50"
                          data-testid={`digest-retry-${d.run_id}`}
                        >
                          <Send size={12}/> {retryingId === d.run_id ? "Reintentando…" : "Reintentar fallidos"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {d.articles && d.articles.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-border2">
                    <p className="eyebrow">Artículos incluidos</p>
                    <ul className="mt-3 space-y-1 text-sm text-muted2">
                      {d.articles.map((a) => (
                        <li key={a.id} className="flex items-baseline gap-3">
                          <span className="text-[0.58rem] tracking-[0.22em] uppercase text-cobalt shrink-0">{a.category}</span>
                          <span>{a.title}</span>
                          {d.cover_article_id === a.id && <span className="text-[0.55rem] tracking-[0.2em] uppercase bg-cobalt/15 text-cobalt px-1.5 py-0.5">Portada</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {d.status && d.status !== "completed" && (
                  <p className="mt-3 text-xs text-terracotta uppercase tracking-widest">Estado: {d.status}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setShowPreview(false)}>
          <div className="bg-white max-w-2xl w-full my-10 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="digest-preview-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border2">
              <p className="eyebrow">Vista previa · Edición del jueves</p>
              <button onClick={() => setShowPreview(false)} className="text-xs uppercase tracking-widest text-subtle" data-testid="digest-preview-close">Cerrar</button>
            </div>
            <iframe title="preview" srcDoc={previewHtml} className="w-full h-[70vh] bg-white border-0" />
          </div>
        </div>
      )}
    </div>
  );
}


/* ────────────────────── SOLICITUDES PANEL (Concierge inbox) ────────────────────── */

function SolicitudesPanel() {
  const [items, setItems] = useState([]);

  const load = () => api.get("/concierge").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const reply = (req) => {
    const message = `Hola ${req.name}, le escribimos del Insider sobre su solicitud de concierge (${req.arrival || ""}${req.departure ? "–" + req.departure : ""}).`;
    openWhatsApp(message);
  };

  return (
    <div className="space-y-8" data-testid="admin-solicitudes-panel">
      <div>
        <h2 className="font-serif text-2xl">Solicitudes de Concierge ({items.length})</h2>
        <p className="mt-2 text-sm text-muted2">Formularios enviados desde la página de Concierge. Cada envío llega también a su correo.</p>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-subtle border border-dashed border-border2 p-6 text-center">Aún no hay solicitudes.</p>
      ) : (
        <div className="space-y-5">
          {items.map((s) => (
            <div key={s.id} className="border border-border2 bg-white p-6" data-testid={`solicitud-${s.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="eyebrow">{s.zone || "Sin zona"} · {s.arrival || "sin fecha"}{s.departure ? ` → ${s.departure}` : ""}</p>
                  <h3 className="mt-2 font-serif text-xl text-ink">{s.name}</h3>
                  <p className="mt-1 text-sm text-subtle">
                    <a href={s.contact.includes("@") ? `mailto:${s.contact}` : `tel:${s.contact}`} className="text-cobalt border-b border-cobalt/40">{s.contact}</a>
                  </p>
                  {s.notes && <p className="mt-4 text-muted2 text-sm leading-relaxed whitespace-pre-line">{s.notes}</p>}
                </div>
                <button
                  onClick={() => reply(s)}
                  className="btn-outline text-xs px-3 py-2 inline-flex items-center gap-2 shrink-0"
                  data-testid={`solicitud-reply-${s.id}`}
                >
                  <MessageCircle size={13}/> Responder por WhatsApp
                </button>
              </div>
              <p className="mt-4 text-[0.6rem] tracking-[0.22em] uppercase text-subtle font-display font-semibold">
                Recibida {new Date(s.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ────────────────────── TRANSCRIPTS PANEL ────────────────────── */

function TranscriptsPanel() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [transcript, setTranscript] = useState(null);

  const load = () => api.get("/admin/chat-sessions").then((r) => setSessions(r.data?.sessions || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const openSession = async (id) => {
    setActive(id);
    try {
      const r = await api.get(`/admin/chat-sessions/${id}`);
      setTranscript(r.data);
    } catch { setTranscript(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-transcripts-panel">
      <div>
        <h2 className="font-serif text-2xl">Conversaciones del chatbot ({sessions.length})</h2>
        <p className="mt-2 text-sm text-muted2">Las últimas 100 sesiones con el asistente del Insider. Ordenadas por actividad más reciente.</p>
      </div>
      {sessions.length === 0 ? (
        <p className="text-sm text-subtle border border-dashed border-border2 p-6 text-center">Aún no hay conversaciones.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5 border border-border2 bg-white overflow-hidden max-h-[70vh] overflow-y-auto">
            {sessions.map((s) => (
              <button
                key={s.session_id}
                onClick={() => openSession(s.session_id)}
                data-testid={`transcript-${s.session_id}`}
                className={`w-full text-left px-5 py-4 border-b border-border2 hover:bg-sand/50 transition-colors ${active === s.session_id ? "bg-sand" : ""}`}
              >
                <p className="eyebrow">{s.message_count} mensaje{s.message_count === 1 ? "" : "s"} · {s.last_user_at ? new Date(s.last_user_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</p>
                <p className="mt-2 font-serif text-sm text-ink line-clamp-2">{s.first_message || "— sin mensaje de usuario —"}</p>
              </button>
            ))}
          </div>
          <div className="md:col-span-7">
            {transcript ? (
              <div className="border border-border2 bg-white p-6 max-h-[70vh] overflow-y-auto" data-testid="transcript-detail">
                <p className="eyebrow">Sesión {transcript.session_id?.slice(0, 10)}…</p>
                <p className="text-xs text-subtle mt-1">
                  Inicio: {transcript.created_at ? new Date(transcript.created_at).toLocaleString("es-MX") : "—"}
                </p>
                <div className="mt-5 space-y-4">
                  {(transcript.messages || []).map((m, i) => (
                    <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                      <div className={m.role === "user"
                        ? "max-w-[85%] bg-sand text-ink text-sm leading-relaxed px-4 py-3 whitespace-pre-line"
                        : "max-w-[90%] text-ink text-sm leading-relaxed font-serif whitespace-pre-line"}>
                        <p className="font-display text-[0.55rem] tracking-[0.25em] uppercase mb-1 opacity-60">
                          {m.role === "user" ? "Lector" : "Insider"} · {m.at ? new Date(m.at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }) : ""}
                        </p>
                        {m.role === "assistant" ? (m.text || "").replace(/\[\[cta:[^\]]+\]\]/g, "").trim() : m.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-subtle border border-dashed border-border2 p-10 text-center">Seleccione una conversación.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

